import { useState, useEffect } from 'react';
import { ProgressBlockItem as ProgressBlockItemType, AnalysisPhase } from '../../../types/conversation';

interface ProgressBlockItemProps {
  item: ProgressBlockItemType;
}

const phaseLabels: Record<AnalysisPhase, string> = {
  clarification: 'Doprecyzowanie',
  exploration: 'Eksploracja',
  analysis: 'Analiza',
  synthesis: 'Synteza',
  complete: 'Zakonczone',
};

const phaseColors: Record<AnalysisPhase, string> = {
  clarification: 'text-blue-600 dark:text-blue-400',
  exploration: 'text-amber-600 dark:text-amber-400',
  analysis: 'text-purple-600 dark:text-purple-400',
  synthesis: 'text-green-600 dark:text-green-400',
  complete: 'text-green-600 dark:text-green-400',
};

export function ProgressBlockItem({ item }: ProgressBlockItemProps) {
  // Auto-expand when streaming, collapse when complete
  const [isExpanded, setIsExpanded] = useState(!item.isComplete);

  // Collapse when block becomes complete
  useEffect(() => {
    if (item.isComplete) {
      setIsExpanded(false);
    }
  }, [item.isComplete]);

  // If still streaming, show expanded view
  if (item.isStreaming) {
    const recentSteps = item.steps.slice(-5);

    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-3 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm font-medium">Analizuje...</span>
            <span className={`text-xs ${phaseColors[item.phase]}`}>
              ({phaseLabels[item.phase]})
            </span>
          </div>
          {recentSteps.length > 0 && (
            <div className="space-y-1">
              {recentSteps.map((step, index) => (
                <div key={index} className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  {step.completed ? (
                    <svg className="w-3 h-3 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3 text-blue-500 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  <span className="truncate">{step.message}</span>
                </div>
              ))}
            </div>
          )}
          {recentSteps.length === 0 && (
            <p className="text-xs text-slate-500 dark:text-slate-400">Rozpoczynam analize...</p>
          )}
        </div>
      </div>
    );
  }

  // Completed - show collapsible view
  return (
    <div className="flex justify-start">
      <div className="max-w-[95%] w-full">
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-3 py-2 flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            <span className="flex items-center gap-2">
              <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{item.steps.length} krokow wykonanych</span>
              <span className={`${phaseColors[item.phase]}`}>
                ({phaseLabels[item.phase]})
              </span>
            </span>
            <svg
              className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isExpanded && (
            <div className="px-3 pb-2 space-y-1">
              {item.steps.map((step, index) => (
                <div key={index} className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{step.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProgressBlockItem;
